import {
  icons as lucideIcons,
  type LucideIcon,
} from 'lucide-react';
import type { CSSProperties } from 'react';
import { normalizeLocale } from '@gift-i18n';
import type {
  AdminProjectTemplate,
  ProjectTemplate,
  TemplateIconColor,
  TemplateIconStyle,
  TemplateTagColorToken,
  TemplateTagDefinition,
} from '@claude-code-api/shared';

export type TemplateIconOption = {
  value: string;
  label: string;
  icon: LucideIcon;
};

function kebabCaseIconName(value: string): string {
  return value
    .replace(/Icon$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

function iconLabel(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

export const TEMPLATE_ICON_OPTIONS: TemplateIconOption[] = Object.entries(lucideIcons)
  .filter(([name]) => !name.endsWith('Icon'))
  .map(([name, icon]) => ({
    value: kebabCaseIconName(name),
    label: iconLabel(kebabCaseIconName(name)),
    icon: icon as LucideIcon,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const TEMPLATE_ICON_STYLE_OPTIONS: Array<{ value: TemplateIconStyle; label: string }> = [
  { value: 'gradient', label: 'Gradient' },
  { value: 'plain', label: 'Plain' },
];

export const TEMPLATE_ICON_COLOR_OPTIONS: Array<{ value: TemplateIconColor; label: string }> = [
  { value: 'slate', label: 'Slate' },
  { value: 'blue', label: 'Blue' },
  { value: 'teal', label: 'Teal' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
  { value: 'violet', label: 'Violet' },
  { value: 'cyan', label: 'Cyan' },
];

const TEMPLATE_ICON_COMPONENTS = new Map<string, LucideIcon>(TEMPLATE_ICON_OPTIONS.map((option) => [option.value, option.icon]));

const TEMPLATE_ICON_STYLES: Record<TemplateIconColor, { plain: string; gradient: string }> = {
  slate: {
    plain: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-200',
    gradient: 'bg-gradient-to-br from-slate-700 to-slate-500 text-white',
  },
  blue: {
    plain: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    gradient: 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white',
  },
  teal: {
    plain: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
    gradient: 'bg-gradient-to-br from-teal-600 to-cyan-500 text-white',
  },
  emerald: {
    plain: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
    gradient: 'bg-gradient-to-br from-emerald-600 to-teal-500 text-white',
  },
  amber: {
    plain: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    gradient: 'bg-gradient-to-br from-amber-500 to-orange-500 text-white',
  },
  rose: {
    plain: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
    gradient: 'bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white',
  },
  violet: {
    plain: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
    gradient: 'bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white',
  },
  cyan: {
    plain: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
    gradient: 'bg-gradient-to-br from-cyan-600 to-sky-500 text-white',
  },
};

export function normalizeTemplateTag(value: string): string {
  return String(value || '').trim().toLowerCase();
}

export function buildTemplateCatalogApiPath(path: string, locale?: string | null): string {
  const normalizedLocale = normalizeLocale(locale);
  if (!normalizedLocale) return path;

  const url = new URL(path, 'http://localhost');
  url.searchParams.set('lang', normalizedLocale);
  return `${url.pathname}${url.search}`;
}

export function getTemplateTagDefinitions(template: ProjectTemplate | AdminProjectTemplate | null | undefined): TemplateTagDefinition[] {
  return Array.isArray(template?.tagDefinitions) ? template.tagDefinitions : [];
}

export function getTemplateSearchText(template: ProjectTemplate): string {
  return [
    template.name,
    template.description,
    ...getTemplateTagDefinitions(template).map((tag) => `${tag.name} ${tag.group?.name || ''} ${tag.slug}`),
  ]
    .join(' ')
    .toLowerCase();
}

type TemplateIconLike = Pick<ProjectTemplate, 'icon' | 'iconStyle' | 'iconColor'> | null | undefined;
type TemplateShowcaseLike = Pick<ProjectTemplate, 'coverImageUrl' | 'screenshotUrls'> | null | undefined;

export function getTemplateIconName(template: TemplateIconLike): string {
  return String(template?.icon || '').trim().toLowerCase();
}

export function getTemplateIconStyle(template: TemplateIconLike): TemplateIconStyle {
  return template?.iconStyle === 'plain' ? 'plain' : 'gradient';
}

export function getTemplateIconColor(template: TemplateIconLike): string {
  return String(template?.iconColor || '').trim() || 'teal';
}

export function getTemplateIconComponent(template: TemplateIconLike) {
  return TEMPLATE_ICON_COMPONENTS.get(getTemplateIconName(template)) || TEMPLATE_ICON_COMPONENTS.get('layout-template') || TEMPLATE_ICON_OPTIONS[0].icon;
}

export function getTemplateIconOption(value: string): TemplateIconOption {
  return TEMPLATE_ICON_OPTIONS.find((option) => option.value === value) || TEMPLATE_ICON_OPTIONS.find((option) => option.value === 'layout-template') || TEMPLATE_ICON_OPTIONS[0];
}

export function getTemplateIconClassName(template: TemplateIconLike): string {
  const color = getTemplateIconColor(template) as TemplateIconColor;
  const style = getTemplateIconStyle(template);
  return TEMPLATE_ICON_STYLES[color]?.[style] || 'text-white';
}

export function getTemplateIconStyleAttribute(template: TemplateIconLike): CSSProperties | undefined {
  const color = getTemplateIconColor(template);
  if (TEMPLATE_ICON_STYLES[color as TemplateIconColor]) return undefined;
  if (getTemplateIconStyle(template) === 'gradient') {
    return { backgroundImage: color };
  }
  return { backgroundColor: color };
}

export function getTemplateTagColorClassName(colorToken: TemplateTagColorToken | undefined): string {
  switch (colorToken) {
    case 'blue':
      return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300';
    case 'teal':
      return 'border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-300';
    case 'emerald':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300';
    case 'amber':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300';
    case 'rose':
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300';
    case 'violet':
      return 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300';
    case 'cyan':
      return 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-900 dark:bg-cyan-950 dark:text-cyan-300';
    case 'slate':
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300';
  }
}

export function getCompactTemplateBadgeClassName(extraClassName?: string): string {
  return [
    'h-5 rounded-sm px-1.5 py-0 text-[10px] font-medium leading-none shadow-none',
    extraClassName || '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function getTemplateScreenshotUrls(template: TemplateShowcaseLike): string[] {
  if (!Array.isArray(template?.screenshotUrls)) return [];
  return template.screenshotUrls
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

export function getTemplatePrimaryImage(template: TemplateShowcaseLike): string | null {
  const screenshots = getTemplateScreenshotUrls(template);
  if (screenshots.length > 0) return screenshots[0];
  const cover = String(template?.coverImageUrl || '').trim();
  return cover || null;
}
